// HealthForm.js
import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useTheme,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Card,
  CardContent
} from "@mui/material";
import { User, Map, FileText, AlertCircle, Heart, CreditCard, Users, Award, Check, Building } from "lucide-react";
import { preexistingConditions } from "./Common";
import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

const indianStates = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];
const nomineeRelations = ["Spouse", "Child", "Parent", "Sibling"];

const toTitleCase = (str) =>
  str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getFieldIcon = (fieldName) => {
  const iconMap = {
    age: <User size={20} />,
    gender: <User size={20} />,
    state: <Map size={20} />,
    preexisting_conditions: <Heart size={20} />,
    has_addon: <FileText size={20} />,
    is_emi_payment: <CreditCard size={20} />,
    nominee_relation: <Users size={20} />,
    has_previous_claims: <AlertCircle size={20} />,
    credit_score: <Award size={20} />,
    tenantId: <Building size={20} />,
    tenantName: <Building size={20} />
  };
  return iconMap[fieldName] || <Check size={20} />;
};

function HealthForm({ setScore, setErrorMessage, setOpenSnackbar, setIsLoading }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    age: "28",
    gender: "Female",
    state: "Tamil Nadu",
    preexisting_conditions: ["None"],
    has_addon: true,
    is_emi_payment: true,
    nominee_relation: "Child",
    has_previous_claims: false,
    credit_score: "720"
  });
  
  // Tenant information state with defaults
  const [tenantInfo, setTenantInfo] = useState({
    tenantId: "1",
    tenantName: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert Yes/No to boolean for specific fields
    if (['has_addon', 'is_emi_payment', 'has_previous_claims'].includes(name)) {
      setFormData({ ...formData, [name]: value === 'Yes' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTenantChange = (e) => {
    const { name, value } = e.target;
    setTenantInfo({ ...tenantInfo, [name]: value });
  };

  const handlePreexistingConditionsChange = (event) => {
    const value = event.target.value;
    let newConditions = typeof value === 'string' ? value.split(',') : value;

    // If "None" is selected, clear all other selections
    if (newConditions.includes("None")) {
      newConditions = ["None"];
    } else {
      // If any other condition is selected, remove "None"
      newConditions = newConditions.filter(condition => condition !== "None");
    }

    setFormData({ ...formData, preexisting_conditions: newConditions });
  };

  const handleHealthFormSubmit = async () => {
    setScore(null);
    setErrorMessage("");
    setIsSubmitting(true);
    setIsLoading(true);

    // Validate tenant information
    if (!tenantInfo.tenantId.trim() || !tenantInfo.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    const age = parseInt(formData.age, 10);
    const creditScore = parseInt(formData.credit_score, 10);

    if (isNaN(age) || age < 18 || age > 100) {
      setErrorMessage("Age must be between 18 and 100.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    if (isNaN(creditScore) || creditScore < 0 || creditScore > 850) {
      setErrorMessage("Credit Score must be between 0 and 850.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    // Validate boolean fields
    if (formData.has_addon === null || formData.is_emi_payment === null || formData.has_previous_claims === null) {
      setErrorMessage("Please answer all Yes/No questions.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Generate tenant token
      const tokenResponse = await generateTenantToken(tenantInfo.tenantId, tenantInfo.tenantName);
      
      // Step 2: Make encrypted API call
      const url = `/api/v1.0/tenants/${tenantInfo.tenantId}/score/health`;
      const score = await makeEncryptedApiCall(url, formData, tokenResponse.token);
      
      setScore(score.output);
    } catch (error) {
      console.error("Error fetching health score:", error);
      let detailedMessage = "An error occurred while fetching the health score.";
      if (error.response && error.response.data && error.response.data.error) {
        detailedMessage = error.response.data.error;
      } else if (error.message) {
        detailedMessage = error.message;
      }
      setErrorMessage(detailedMessage);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Tenant Information Card */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Building size={20} />
              Tenant Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getFieldIcon('tenantId')}
                  <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                    Tenant ID
                  </Typography>
                </Box>
                <TextField
                  name="tenantId"
                  value={tenantInfo.tenantId}
                  onChange={handleTenantChange}
                  fullWidth
                  required
                  placeholder="Enter Tenant ID"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getFieldIcon('tenantName')}
                  <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                    Tenant Name
                  </Typography>
                </Box>
                <TextField
                  name="tenantName"
                  value={tenantInfo.tenantName}
                  onChange={handleTenantChange}
                  fullWidth
                  required
                  placeholder="Enter Tenant Name"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Rest of the form fields with defaults */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('age')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Age
          </Typography>
        </Box>
        <TextField
          name="age"
          type="number"
          inputProps={{ min: 18, max: 100 }}
          value={formData.age}
          onChange={handleChange}
          fullWidth
          required
          placeholder="Enter age (18-100)"
          variant="outlined"
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          disabled={isSubmitting}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('gender')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Gender
          </Typography>
        </Box>
        <RadioGroup
          row
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          sx={{ '& .MuiRadio-root': { color: theme.palette.primary.main } }}
        >
          <FormControlLabel value="Male" control={<Radio disabled={isSubmitting} size="small" />} label="Male" />
          <FormControlLabel value="Female" control={<Radio disabled={isSubmitting} size="small" />} label="Female" />
        </RadioGroup>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('state')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            State
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="state"
            value={formData.state}
            onChange={handleChange}
            displayEmpty
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxHeight: 300 } } }}
          >
            <MenuItem value="" disabled>Select State</MenuItem>
            {indianStates.map((state) => (
              <MenuItem key={state} value={state}>{state}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('nominee_relation')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Nominee Relation
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="nominee_relation"
            value={formData.nominee_relation}
            onChange={handleChange}
            displayEmpty
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            <MenuItem value="" disabled>Select Relation</MenuItem>
            {nomineeRelations.map((relation) => (
              <MenuItem key={relation} value={relation}>{relation}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('credit_score')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Credit Score
          </Typography>
        </Box>
        <TextField
          name="credit_score"
          type="number"
          inputProps={{ min: 0, max: 850 }}
          value={formData.credit_score}
          onChange={handleChange}
          fullWidth
          required
          placeholder="Enter score (0-850)"
          variant="outlined"
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          disabled={isSubmitting}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('preexisting_conditions')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Preexisting Conditions
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            multiple
            value={formData.preexisting_conditions}
            onChange={handlePreexistingConditionsChange}
            input={<OutlinedInput sx={{ borderRadius: 2 }} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.primary.contrastText
                      }
                    }}
                  />
                ))}
              </Box>
            )}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  maxHeight: 300
                }
              }
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>Select preexisting conditions</em>
            </MenuItem>
            {preexistingConditions.map((condition) => (
              <MenuItem key={condition} value={condition}>
                <Checkbox
                  checked={formData.preexisting_conditions.indexOf(condition) > -1}
                  sx={{ color: theme.palette.primary.main }}
                />
                <ListItemText primary={condition} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {['has_addon', 'is_emi_payment', 'has_previous_claims'].map((field) => (
        <Grid item xs={12} sm={6} key={field}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {getFieldIcon(field)}
            <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
              {toTitleCase(field)}
            </Typography>
          </Box>
          <RadioGroup
            row
            name={field}
            value={formData[field] === null ? '' : (formData[field] ? 'Yes' : 'No')}
            onChange={handleChange}
            sx={{ '& .MuiRadio-root': { color: theme.palette.primary.main } }}
          >
            <FormControlLabel value="Yes" control={<Radio disabled={isSubmitting} size="small" />} label="Yes" />
            <FormControlLabel value="No" control={<Radio disabled={isSubmitting} size="small" />} label="No" />
          </RadioGroup>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleHealthFormSubmit}
          fullWidth
          disabled={isSubmitting}
          size="large"
          sx={{
            mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
            background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
            boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
            '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
            transition: 'all 0.3s ease',
            '&.Mui-disabled': {
                background: theme.palette.grey[300],
                boxShadow: 'none',
                color: theme.palette.grey[500],
                cursor: 'not-allowed'
            }
          }}
        >
          {isSubmitting ? "Calculating..." : "Calculate Health Insurance Score"}
        </Button>
      </Grid>
    </Grid>
  );
}

export default HealthForm;
